import React, { useState, useRef } from 'react';
import { CaptureOverlay } from '../shared';
import type { ScreenMode } from './useScreenRecorder';

interface ScreenNotesProps {
  taskName: string;
  videoUrl: string | null;
  duration: number;
  mode: ScreenMode;
  onSubmit: (notes: string) => void;
  onSaveDraft: (notes: string) => void;
  onClose: () => void;
  initialNotes?: string;
}

const MAX_CHARS = 2000;

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const ScreenNotes: React.FC<ScreenNotesProps> = ({
  taskName,
  videoUrl,
  duration,
  mode,
  onSubmit,
  onSaveDraft,
  onClose,
  initialNotes,
}) => {
  const [notes, setNotes] = useState(initialNotes || '');
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const togglePlay = () => {
    if (!videoRef.current || !videoUrl) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const badgeLabel = mode === 'screen-cam' ? 'SCREEN + CAM' : 'SCREEN';

  return (
    <CaptureOverlay taskName={taskName} onClose={onClose}>
      <div className="co-sheet">
        <div className="co-sheet-handle" />
        <div className="co-sheet-head">
          <div className="co-sheet-title">Add a Note</div>
        </div>

        <div className="co-sheet-body">
          {/* Attached recording (small card) */}
          <div>
            <div className="co-attached-label">Attached Recording</div>
            <div className="sc-video-card sc-video-card-sm">
              {videoUrl && (
                <video
                  ref={videoRef}
                  src={videoUrl}
                  onEnded={() => setIsPlaying(false)}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              )}
              <div className="sc-video-play sc-video-play-sm" onClick={togglePlay}>
                {isPlaying ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                )}
              </div>
              <div className="sc-video-dur">{formatTime(duration)}</div>
              <div className="sc-video-badge">{badgeLabel}</div>
            </div>
          </div>

          {/* Notes textarea */}
          <div className="co-note-wrap">
            <div className="co-note-label">Optional note</div>
            <textarea
              className="co-textarea"
              placeholder="Add context, reactions, or anything else…"
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value.slice(0, MAX_CHARS))}
            />
            <div className="co-charcount">{notes.length} / {MAX_CHARS}</div>
          </div>
        </div>

        <div className="co-sheet-actions">
          <button className="co-btn-ember" onClick={() => onSubmit(notes)}>
            Submit Task
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="20,6 9,17 4,12" />
            </svg>
          </button>
          <button className="co-btn-ghost" onClick={() => onSaveDraft(notes)}>
            Save Draft
          </button>
        </div>
      </div>
    </CaptureOverlay>
  );
};
